// "Frequently bought together" checklist in the cart drawer: the merchant's pick (Theme settings -> Cart drawer
// upsell, pre-ticked) first, then Shopify recommendations for the newest cart item (complementary first, then
// related) up to 3 rows, excluding what's already in the cart. One button adds every ticked item and re-renders
// the theme's drawer; its label shows the count and total.
if (!customElements.get('lp-cart-suggestions')) {
  const requests = {}; // per product id, shared across drawer re-renders

  const fetchCards = (url, productId) =>
    Promise.all(
      ['complementary', 'related'].map((intent) =>
        fetch(`${url}?product_id=${productId}&limit=6&intent=${intent}&section_id=lp-cart-suggestions`)
          .then((r) => (r.ok ? r.text() : ''))
          .catch(() => '')
      )
    ).then((pages) => {
      const seen = new Set();
      const cards = [];
      pages.forEach((html) =>
        new DOMParser().parseFromString(html, 'text/html').querySelectorAll('.rh-sugg__card').forEach((card) => {
          if (!seen.has(card.dataset.productId)) {
            seen.add(card.dataset.productId);
            cards.push(card);
          }
        })
      );
      return cards;
    });

  customElements.define(
    'lp-cart-suggestions',
    class extends HTMLElement {
      connectedCallback() {
        this.list = this.querySelector('[data-list]');
        this.button = this.querySelector('[data-add]');
        this.label = this.querySelector('[data-add-label]');
        this.status = this.querySelector('[data-add-status]');
        this.baseLabel = this.label.textContent.trim();
        this.inCart = new Set((this.dataset.inCart || '').split(',').filter(Boolean));
        this.addEventListener('change', (e) => {
          if (!e.target.matches('.rh-sugg__check')) return;
          this.touched = true;
          this.update();
        });
        this.button.addEventListener('click', () => this.add());
        this.update();
        this.load();
      }

      checked() {
        return [...this.list.querySelectorAll('.rh-sugg__check:checked')];
      }

      // "Add 2 items · Rs. 570.00" using the shop's own money format.
      update() {
        const picked = this.checked();
        const total = picked.reduce((sum, c) => sum + Number(c.dataset.price), 0);
        this.button.disabled = !picked.length;
        const text = picked.length
          ? `${this.baseLabel} ${picked.length} ${picked.length === 1 ? 'item' : 'items'} · ${this.money(total)}`
          : 'Tick an item to add';
        this.label.textContent = text;
        if (this.status) this.status.textContent = text;
      }

      money(cents) {
        const format = this.dataset.moneyFormat || '{{amount}}';
        const fixed = (cents / 100).toFixed(2);
        const [whole, dec] = fixed.split('.');
        const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, kind) => {
          if (kind === 'amount_no_decimals') return grouped;
          if (kind === 'amount_with_comma_separator') return `${whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${dec}`;
          if (kind === 'amount_no_decimals_with_comma_separator') return whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
          return `${grouped}.${dec}`;
        });
      }

      async load() {
        const id = this.dataset.productId;
        if (!id || !this.dataset.url) return;
        requests[id] = requests[id] || fetchCards(this.dataset.url, id);
        // The merchant's pick (server-rendered fallback card) stays first; Shopify's picks fill the rest.
        const pinned = [...this.list.querySelectorAll('.rh-sugg__card')];
        const taken = new Set([...this.inCart, ...pinned.map((c) => c.dataset.productId)]);
        const extra = (await requests[id]).filter((c) => !taken.has(c.dataset.productId)).slice(0, 3 - pinned.length);
        if (!extra.length || !this.isConnected) return;
        this.list.append(...extra.map((c) => document.importNode(c, true)));
        // Nothing pre-ticked (e.g. the merchant's pick is already in the cart): tick the top suggestion.
        if (!this.checked().length && !this.touched) this.list.querySelector('.rh-sugg__check').checked = true;
        this.hidden = false;
        this.update();
      }

      async add() {
        const picked = this.checked();
        if (!picked.length) return;
        const drawer = document.querySelector('cart-drawer');
        const button = this.button;
        button.disabled = true;
        button.classList.add('is-loading');
        this.querySelector('.rh-sugg__error')?.remove();
        try {
          const response = await fetch(`${(window.routes && window.routes.cart_add_url) || '/cart/add'}.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              items: picked.map((c) => ({ id: Number(c.value), quantity: 1 })),
              sections: drawer ? drawer.getSectionsToRender().map((s) => s.id) : [],
              sections_url: window.location.pathname,
            }),
          });
          const state = await response.json();
          if (!response.ok) throw new Error(state.description || state.message || 'Could not add to cart');
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'lp-cart-suggestions', cartData: state });
          }
          if (drawer && state.sections) drawer.renderContents(state);
          else window.location.href = (window.routes && window.routes.cart_url) || '/cart';
        } catch (error) {
          button.disabled = false;
          button.classList.remove('is-loading');
          const note = document.createElement('p');
          note.className = 'rh-sugg__error';
          note.setAttribute('role', 'alert');
          note.textContent = error.message;
          button.after(note);
        }
      }
    }
  );
}
